export type VideoEmbed =
  | {
      type: "vimeo" | "youtube";
      url: string;
    }
  | {
      type: "file";
      url: string;
    };

const DIRECT_VIDEO_PATTERN = /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i;

export function isPdfUrl(url: string): boolean {
  return /\.pdf(\?|#|$)/i.test(url.trim());
}

function isDirectVideoUrl(url: string): boolean {
  const trimmed = url.trim();
  return DIRECT_VIDEO_PATTERN.test(trimmed);
}

function extractYouTubeVideoId(url: string): string | null {
  const cleanUrl = url.trim();
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^#&?]*)/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];

  for (const pattern of patterns) {
    const match = cleanUrl.match(pattern);
    if (match?.[1] && match[1].length === 11) {
      return match[1];
    }
  }

  return null;
}

function buildYouTubeEmbedUrl(videoId: string, autoplay = false): string {
  const params = new URLSearchParams({
    enablejsapi: "1",
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
  });

  if (autoplay) {
    params.set("autoplay", "1");
  }

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

function buildVimeoEmbedUrl(url: string, autoplay = false): string | null {
  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.includes("player.vimeo.com/video/")) {
    const embedUrl = new URL(trimmed);
    embedUrl.searchParams.set("badge", "0");
    embedUrl.searchParams.set("autopause", "0");
    embedUrl.searchParams.set("player_id", "0");
    embedUrl.searchParams.set("app_id", "58479");
    if (autoplay) {
      embedUrl.searchParams.set("autoplay", "1");
    }
    return embedUrl.toString();
  }

  const vimeoMatch = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-f0-9]+))?/i);
  if (!vimeoMatch) {
    return null;
  }

  const [, id, hash] = vimeoMatch;
  const params = new URLSearchParams({
    badge: "0",
    autopause: "0",
    player_id: "0",
    app_id: "58479",
  });

  if (hash) {
    params.set("h", hash);
  }

  if (autoplay) {
    params.set("autoplay", "1");
  }

  return `https://player.vimeo.com/video/${id}?${params.toString()}`;
}

export function getVideoEmbed(videoUrl: string, autoplay = false): VideoEmbed | null {
  const trimmed = videoUrl.trim();
  if (!trimmed || isPdfUrl(trimmed)) {
    return null;
  }

  const vimeoUrl = buildVimeoEmbedUrl(trimmed, autoplay);
  if (vimeoUrl) {
    return { type: "vimeo", url: vimeoUrl };
  }

  const youtubeId = extractYouTubeVideoId(trimmed);
  if (youtubeId) {
    return {
      type: "youtube",
      url: buildYouTubeEmbedUrl(youtubeId, autoplay),
    };
  }

  if (isDirectVideoUrl(trimmed)) {
    return { type: "file", url: trimmed };
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return { type: "file", url: trimmed };
    }
  } catch {
    return null;
  }

  return null;
}

export function canPlayTutorialVideo(
  videoUrl?: string,
  blockedUrl?: string,
): boolean {
  if (!videoUrl) {
    return false;
  }

  const trimmed = videoUrl.trim();
  if (!trimmed || trimmed === blockedUrl?.trim() || isPdfUrl(trimmed)) {
    return false;
  }

  return Boolean(getVideoEmbed(trimmed));
}
